import * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import type { Course_Course as Course, Maybe } from '__generated__/graphql-types';

import CourseSlugAndName from 'bundles/enterprise-legacy-learner-home/queries/CourseSlugAndName.graphql';
import type {
  CourseSlugAndNameQuery,
  CourseSlugAndNameQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/CourseSlugAndName';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import { useNaptime } from 'bundles/naptimejs';
import type { AddedProps as PropsFromNaptimeContainer } from 'bundles/naptimejs';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import ProgramCurriculumProductsV1 from 'bundles/naptimejs/resources/programCurriculumProducts.v1';
import EnterpriseEnrolledCourseContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseEnrolledCourseContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

export type CourseDetails =
  | Maybe<
      {
        __typename?: 'Course_Course' | undefined;
      } & Pick<Course, 'id' | 'slug' | 'name'>
    >
  | undefined;

type PropsFromNaptime = PropsFromNaptimeContainer & {
  enrolledCourses?: ProgramCurriculumProductsV1[];

  /* Start of Enterprise: enableAutoEnrollment experiment */
  enrolledS12ns?: ProgramCurriculumProductsV1[];
  courseDetails?: CourseDetails;
  /* End of Enterprise: enableAutoEnrollment experiment */
};

type Props = {
  children: React.ReactNode;
};

const getPreviousCourseId = (
  inProgressCourse: ProgramCurriculumProductsV1 | undefined,
  enrolledS12ns: ProgramCurriculumProductsV1[] | undefined
) => {
  let currentCourseIndex = -1;
  const enrolledS12n =
    enrolledS12ns && inProgressCourse
      ? enrolledS12ns.find((s12n) => {
          // Generate a list of courseIds for convenience
          const courseIdMap = s12n?.productState?.definition?.courseStates.map((course: any) => course.courseId);
          // Find the index of the newly enrolled course so we can be sure its
          // part of an enrolled s12n
          const courseIndex = courseIdMap.indexOf(inProgressCourse.productItemId);

          if (courseIndex !== -1) {
            // Find the enrolled index so we can look back 1 index
            // at the completed course.
            currentCourseIndex = courseIndex;
            return true;
          }

          return false;
        })
      : null;

  const previousCourse = enrolledS12n?.productState?.definition?.courseStates?.[currentCourseIndex - 1] ?? null;
  const currentCourse = enrolledS12n?.productState?.definition?.courseStates?.[currentCourseIndex] ?? null;
  // Ensure last course is completed, current course is unstarted
  const previousCourseId =
    currentCourse?.lastUpdatedAt === 0 && previousCourse?.state === 'COMPLETED' ? previousCourse.courseId : null;

  return {
    previousCourseId,
    enrolledS12n,
  };
};

const EnterpriseEnrolledCourseProvider = ({ children }: Props) => {
  const program = useProgram();
  const programId = program?.id;

  // This experiment treatment will populate data for enrolledS12ns and courseDetails
  const isAutoEnrollEnabled =
    EnterpriseExperiments.preview('enableAutoEnrollment') === 'enableCourseHomeAutoEnroll' ||
    EnterpriseExperiments.preview('enableAutoEnrollment') === 'enableFirstCourseItemAutoEnroll';

  const {
    data: { enrolledCourses, enrolledS12ns },
  } = useNaptime<PropsFromNaptime>(() => {
    if (!programId) {
      return {};
    }
    return {
      enrolledCourses: ProgramCurriculumProductsV1.finder('latestEnrolledCourseActivities', {
        params: { programId, limit: 1 },
        required: false,
      }),
      enrolledS12ns: isAutoEnrollEnabled
        ? ProgramCurriculumProductsV1.finder('enrolledS12ns', {
            params: { programId, limit: 10 },
            required: false,
          })
        : [],
    };
  }, [programId]);

  const inProgressCourse = enrolledCourses
    ? enrolledCourses.find((course: ProgramCurriculumProductsV1) => !course.isCompleted)
    : null;
  const enrolledS12nsIds = (enrolledS12ns || []).map((s12n) => s12n.id);

  const { previousCourseId, enrolledS12n } = useMemo(() => {
    return getPreviousCourseId(inProgressCourse, enrolledS12ns);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [inProgressCourse?.id, ...enrolledS12nsIds]);

  const { data } = useQuery<CourseSlugAndNameQuery, CourseSlugAndNameQueryVariables>(CourseSlugAndName, {
    variables: {
      id: previousCourseId,
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: !previousCourseId || !programId,
  });

  return (
    <EnterpriseEnrolledCourseContext.Provider
      value={{
        enrolledCourses,
        inProgressCourse,
        enrolledS12n,
        courseDetails: data?.Course?.queryById,
      }}
    >
      {children}
    </EnterpriseEnrolledCourseContext.Provider>
  );
};

export default EnterpriseEnrolledCourseProvider;
